import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';

import { crmQueryKeys } from '@/lib/crm/query/queries';
import type { CrmObjectListSentinelPage } from '@/lib/crm/store';
import { submitEventsOptimistic } from '@/lib/crm/store/submitEventsOptimistic';
import { logger } from '@/lib/logger';
import { TASK_STAGE_ASSOCIATION_TYPE_NAME } from '@/pages/calendar/utils/taskUtils';
import type { CrmObject, EntityTypeName } from '@/types/crm';

// ahoy_task is not yet in SYSTEM_ENTITY_TYPE_NAMES; cast mirrors the
// established pattern (see lib/dashboard/queries.ts).
const TASK_ENTITY_TYPE = 'ahoy_task' as EntityTypeName;

export interface UseCompleteTaskResult {
  completeTask: (
    task: CrmObject,
    completedStageEntityId: string,
    userId: string
  ) => Promise<void>;
  moveTaskToStage: (
    task: CrmObject,
    targetStageEntityId: string,
    userId: string,
    options: { removeFromList: boolean }
  ) => Promise<void>;
  isCompleting: (taskEntityId: string) => boolean;
}

/**
 * Returns `{ completeTask, moveTaskToStage, isCompleting }` for moving a task
 * between pipeline stages via the optimistic events API. Mirrors
 * `updateRecordStageAsync` from `useMemoRecords` — same single-cardinality
 * replacement approach, same error handling contract. `completeTask` is a
 * thin wrapper over `moveTaskToStage` for the Complete button's terminal
 * move; the stage badge uses `moveTaskToStage` directly for non-terminal
 * moves.
 */
export function useCompleteTask(orgId: number): UseCompleteTaskResult {
  const queryClient = useQueryClient();
  const inFlightRef = useRef<Set<string>>(new Set());
  // Force a re-render when in-flight state changes so callers can react to
  // `isCompleting` toggling.
  const [, forceRender] = useState(0);

  const isCompleting = useCallback(
    (taskEntityId: string) => inFlightRef.current.has(taskEntityId),
    []
  );

  const moveTaskToStage = useCallback(
    async (
      task: CrmObject,
      targetStageEntityId: string,
      userId: string,
      options: { removeFromList: boolean }
    ): Promise<void> => {
      const currentStage =
        task.associations?.find(
          a => a.association_type_name === TASK_STAGE_ASSOCIATION_TYPE_NAME
        )?.target_entity_id ?? null;

      // No-op when the stage is already the target stage.
      if (targetStageEntityId === currentStage) {
        return;
      }

      if (inFlightRef.current.has(task.entity_id)) {
        return;
      }
      inFlightRef.current.add(task.entity_id);
      forceRender((n: number) => n + 1);

      const key = crmQueryKeys.objects(orgId, TASK_ENTITY_TYPE);
      await queryClient.cancelQueries({ queryKey: key });

      // Track which query keys had this task's id removed — not a body
      // snapshot, since the object itself never leaves the CRM store, only
      // its id leaves the list's sentinel `ids` array. A full-list snapshot
      // would resurrect other tasks that were legitimately removed by a
      // different in-flight call once this call's rollback restores it.
      const removedFromQueryKeys = new Set<string>();

      // A non-terminal move keeps the task on the dashboard; only a
      // terminal move removes it.
      if (options.removeFromList) {
        queryClient
          .getQueriesData<CrmObjectListSentinelPage>({ queryKey: key })
          .forEach(([queryKey, data]) => {
            if (
              !data ||
              !Array.isArray(data.ids) ||
              !data.ids.includes(task.entity_id)
            ) {
              return;
            }
            removedFromQueryKeys.add(JSON.stringify(queryKey));
            queryClient.setQueryData<CrmObjectListSentinelPage>(queryKey, {
              ...data,
              ids: data.ids.filter(id => id !== task.entity_id),
            });
          });
      }

      try {
        await submitEventsOptimistic({
          orgId,
          userId,
          entityTypeName: TASK_ENTITY_TYPE,
          primaryEntityId: task.entity_id,
          fallbackSeq: task.last_event_seq,
          singleCardinalityReplacements: currentStage
            ? [
                {
                  entityId: task.entity_id,
                  associationTypeName: TASK_STAGE_ASSOCIATION_TYPE_NAME,
                  priorTargetEntityId: currentStage,
                  newTargetEntityId: targetStageEntityId,
                },
              ]
            : undefined,
          build: builder => {
            builder.associationAdded(
              task.entity_id,
              TASK_ENTITY_TYPE,
              TASK_STAGE_ASSOCIATION_TYPE_NAME,
              targetStageEntityId
            );
          },
        });
        // Prefix invalidation (no `exact: true`) so both the dashboard's
        // todayTasksQueryOptions and the calendar's inline task query
        // refetch, even though they're separate cache entries with
        // differing filter params under the same `ahoy_task` key prefix —
        // same technique as useDismissReminder.ts's reminder invalidation.
        // This still reconciles the optimistic cache state with the
        // server's actual filtered result on the next background refetch;
        // the optimistic removal above is what makes the UI correct
        // immediately.
        queryClient.invalidateQueries({ queryKey: key });
      } catch (err: unknown) {
        const error = err instanceof Error ? err : new Error(String(err));
        // Re-insert this id into the cache as it stands right now, not a
        // stale pre-call snapshot — other completions may have finished in
        // the interim and their removals must survive.
        queryClient
          .getQueriesData<CrmObjectListSentinelPage>({ queryKey: key })
          .forEach(([queryKey, data]) => {
            const wasRemoved = removedFromQueryKeys.has(
              JSON.stringify(queryKey)
            );
            if (
              !wasRemoved ||
              !data ||
              !Array.isArray(data.ids) ||
              data.ids.includes(task.entity_id)
            ) {
              return;
            }
            queryClient.setQueryData<CrmObjectListSentinelPage>(queryKey, {
              ...data,
              ids: [...data.ids, task.entity_id],
            });
          });
        logger.debug(
          'useCompleteTask: restored entity into cache after failed completion',
          { org_id: orgId, entity_id: task.entity_id }
        );
        toast.error(
          options.removeFromList
            ? "We couldn't complete that task. Try again."
            : "We couldn't change that task's stage. Try again."
        );
        logger.error('useCompleteTask: task completion failed', error, {
          org_id: orgId,
          entity_id: task.entity_id,
          target_stage: targetStageEntityId,
        });
        throw err;
      } finally {
        inFlightRef.current.delete(task.entity_id);
        forceRender((n: number) => n + 1);
      }
    },
    [orgId, queryClient]
  );

  const completeTask = useCallback(
    (task: CrmObject, completedStageEntityId: string, userId: string) =>
      moveTaskToStage(task, completedStageEntityId, userId, {
        removeFromList: true,
      }),
    [moveTaskToStage]
  );

  return { completeTask, moveTaskToStage, isCompleting };
}
